/**
 * SeasonalityDepartmentSection
 *
 * One collapsible department block of the Monthly Seasonality view: header, the per-department
 * rows (units / gross / F&I, sales + gross with pay-type breakdown, rental income, other income),
 * the bottom-up expense categories with account drill-down, totals and the comparison chart.
 * Extracted verbatim from SeasonalityModeView.renderDepartment.
 */

import React, { useState } from 'react';
import { MonthlyTotals, MONTH_NAMES, getMonthValue, createEmptyMonthlyTotals, addMonthlyTotals } from '../../types';
import { DepartmentTemplate, ExpenseCategory, resolveAccountsForDepartment } from '../../config/docTemplate';
import { ROW_COLORS, CATEGORY_COLORS, SeasonalityModeViewProps, MonthlyAdjustmentContext } from './seasonalityModeTypes';
import { ProjectionCalculatedRecord } from '../../types/projections';
import { MonthlyAdjustmentEditor, AdjustableRow } from './MonthlyAdjustmentEditor';
import { getVarianceColor } from './seasonalityModeHelpers';
import { MonthlyDataRow, UnitDataRow } from './SeasonalityRowComponents';
import {
  DepartmentHeader,
  DepartmentTableHeader,
  VehicleTotalUnitsRow,
  OthersIncomeContent,
  DepartmentComparisonChartSection,
} from './DepartmentContentRenderers';
import { applySeasonality } from '../../utils/seasonalityCalculation';
import { SeasonalityModeData } from './useSeasonalityModeData';
import { DepartmentViewData } from '../../types/projectionViewData';

/** Expand/collapse + comparison UI state owned by SeasonalityModeView */
export interface SeasonalityUiState {
  expandedDepartments: Set<string>;
  expandedCategories: Set<string>;
  toggleDepartment: (deptName: string) => void;
  toggleCategory: (key: string) => void;
  visibleActualMonths: Set<number>;
  toggleActualMonth: (month: number) => void;
  expandedCharts: Set<string>;
  toggleChart: (sectionKey: string) => void;
}

interface SeasonalityDepartmentSectionProps extends Pick<SeasonalityModeViewProps,
  | 'targetYear' | 'formatCurrency' | 'actualData' | 'expensesByAccount'
  | 'getProjectionValue' | 'getTotalProjectedUnits' | 'getCalculatedGross' | 'getServiceDeptProjections'> {
  dept: DepartmentTemplate;
  comparisonMode: boolean;
  isConsolidated: boolean;
  data: SeasonalityModeData;
  ui: SeasonalityUiState;
  getViewDataDepartment: (deptName: string) => DepartmentViewData | null;
  // Stored CALCULATED records (for the adjusted badge + the editor's base/current months)
  calculations?: ProjectionCalculatedRecord[];
  monthlyAdjustment?: MonthlyAdjustmentContext;
}

/** Row labels in the editor, by department shape */
const ADJUSTABLE_ROW_LABELS: Record<string, string> = {
  GROSS: 'Gross', FI_INCOME: 'F&I', UNITS: 'Units', RENTAL_INCOME: 'Rental Income',
};

export const SeasonalityDepartmentSection: React.FC<SeasonalityDepartmentSectionProps> = ({
  dept: deptOriginal,
  targetYear,
  formatCurrency,
  actualData,
  expensesByAccount,
  getProjectionValue,
  getTotalProjectedUnits,
  getCalculatedGross,
  getServiceDeptProjections,
  comparisonMode,
  isConsolidated,
  data,
  ui,
  getViewDataDepartment,
  calculations,
  monthlyAdjustment,
}) => {
  const {
    extraDisplayAccountCodes,
    inputAccountCodes,
    calculationsMap,
    actualAccountMap,
    actualUnitCounts,
    getActualDeptGross,
    getActualDeptExpenses,
    getActualFrontGross,
    getActualFIGross,
    getActualDeptSales,
    getActualDeptProfit,
    getServicePayTypeBreakdown,
    getPartsPayTypeBreakdown,
    getActualAccountValue,
    getStoredMonthlyDistribution,
    accountMap2023,
    accountMap2024,
    accountMap2025,
    hasAccountData,
    getDeptGrossSeasonalityWeights,
    getDeptExpenseSeasonalityWeights,
    getBrandUnitSeasonalityWeights,
    getDeptUnitSeasonalityWeights,
    getAccountSeasonalityWeights,
    getCategoryMonthlyExpense,
    getProjectedUnitsByBrand,
  } = data;
  const {
    expandedDepartments, expandedCategories, toggleDepartment, toggleCategory,
    visibleActualMonths, toggleActualMonth, expandedCharts, toggleChart,
  } = ui;

  // Executive monthly adjustments: which row the editor is open on (null = closed)
  const [editorMetric, setEditorMetric] = useState<string | null>(null);
  // Not in comparison mode (actual columns double the table) and never on consolidated
  const canAdjust = !!monthlyAdjustment?.canAdjust && !isConsolidated && !comparisonMode;
  const storedRecord = (metric: string): ProjectionCalculatedRecord | undefined =>
    calculations?.find(c => c.department === deptOriginal.name && c.metric === metric);
  const adjustableRows: AdjustableRow[] = (deptOriginal.hasFandI
    ? ['GROSS', 'FI_INCOME', 'UNITS']
    : deptOriginal.name === 'RENTAL' ? ['RENTAL_INCOME'] : deptOriginal.hasRevenue ? ['GROSS'] : [])
    .map(metric => ({ metric, record: storedRecord(metric) }))
    .filter((r): r is { metric: string; record: ProjectionCalculatedRecord } => !!r.record)
    .map(r => ({ metric: r.metric, label: deptOriginal.hasFandI && r.metric === 'GROSS' ? 'Front Gross' : ADJUSTABLE_ROW_LABELS[r.metric], record: r.record }));
  // Pencil (when allowed) + "adjusted" badge (whenever a direct adjustment is stored) after a row label
  const adjustmentAdornment = (metric: string): React.ReactNode => {
    const record = storedRecord(metric);
    const adj = record?.manualAdjustment;
    const badge = adj && !adj.derived ? (
      <span
        className="ml-1 px-1 rounded bg-indigo-100 text-indigo-800 text-[10px] font-semibold uppercase no-print"
        title={`Adjusted by ${adj.adjustedByName} on ${new Date(adj.adjustedAt).toLocaleDateString('en-US')}`}
      >
        adj
      </span>
    ) : null;
    const pencil = canAdjust && record && adjustableRows.some(r => r.metric === metric) ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditorMetric(metric); }}
        className="ml-1 text-gray-400 hover:text-blue-600 no-print"
        title="Adjust months (Executive)"
        aria-label={`Adjust ${deptOriginal.displayName} ${ADJUSTABLE_ROW_LABELS[metric] || metric} months`}
      >
        ✎
      </button>
    ) : null;
    return badge || pencil ? <>{badge}{pencil}</> : null;
  };

  // Render a row with monthly data - delegates to MonthlyDataRow component
  const renderMonthlyRow = (
    label: string,
    monthly: MonthlyTotals,
    annualTotal: number,
    colorClass: string,
    isBold: boolean = false,
    indent: number = 0,
    actualMonthly?: MonthlyTotals | null,
    actualAnnual?: number | null,
    isExpense: boolean = false,
    labelAdornment?: React.ReactNode
  ) => (
    <MonthlyDataRow
      label={label}
      monthly={monthly}
      annualTotal={annualTotal}
      colorClass={colorClass}
      isBold={isBold}
      indent={indent}
      actualMonthly={actualMonthly}
      actualAnnual={actualAnnual}
      isExpense={isExpense}
      comparisonMode={comparisonMode}
      visibleActualMonths={visibleActualMonths}
      formatCurrency={formatCurrency}
      labelAdornment={labelAdornment}
    />
  );

  // Render unit counts row - delegates to UnitDataRow component
  const renderUnitMonthlyRow = (
    label: string,
    monthly: MonthlyTotals,
    annualTotal: number,
    colorClass: string,
    indent: number = 0,
    actualMonthly?: MonthlyTotals | null,
    actualAnnual?: number | null
  ) => (
    <UnitDataRow
      label={label}
      monthly={monthly}
      annualTotal={annualTotal}
      colorClass={colorClass}
      indent={indent}
      actualMonthly={actualMonthly}
      actualAnnual={actualAnnual}
      comparisonMode={comparisonMode}
      visibleActualMonths={visibleActualMonths}
      formatCurrency={formatCurrency}
    />
  );

  // Render expense category using pre-calculated monthly values (bottom-up aggregation)
  const renderExpenseCategory = (
    dept: DepartmentTemplate,
    category: ExpenseCategory,
    categoryData: { annual: number; monthly: MonthlyTotals; accountMonthly: Map<string, MonthlyTotals> },
    projectedDeptGross: number
  ) => {
    const categoryKey = `${dept.name}-${category.name}`;
    const isExpanded = expandedCategories.has(categoryKey);

    // Get accounts with data
    const accountsWithData = category.accounts.filter(acc => {
      return hasAccountData(acc.code);
    });

    const categoryTotal = categoryData.annual;
    const monthlyCategory = categoryData.monthly;

    if (categoryTotal === 0 && accountsWithData.length === 0 && category.accounts.length === 0) return null;

    const colorClass = CATEGORY_COLORS[category.name] || ROW_COLORS.otherExpense;

    return (
      <React.Fragment key={categoryKey}>
        {/* Category Header Row */}
        <tr
          className={`${colorClass} border-b border-gray-200 ${category.accounts.length > 0 ? 'cursor-pointer hover:opacity-90' : ''}`}
          onClick={() => category.accounts.length > 0 && toggleCategory(categoryKey)}
        >
          <td className={`min-w-[180px] px-3 py-2 text-sm sticky left-0 ${colorClass}`} style={{ paddingLeft: '28px' }}>
            <div className="flex items-center">
              {category.accounts.length > 0 && (
                <span className="mr-2 text-gray-500 w-4 flex-shrink-0">
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
              {category.accounts.length === 0 && <span className="mr-2 w-4 flex-shrink-0"></span>}
              <span>{category.displayName}</span>
              {category.accounts.length > 0 && (
                <span className="ml-2 text-xs text-gray-500">({category.accounts.length})</span>
              )}
            </div>
          </td>
          {MONTH_NAMES.map((_, i) => {
            const monthNum = i + 1;
            const showActual = comparisonMode && visibleActualMonths.has(monthNum);
            const projValue = getMonthValue(monthlyCategory, monthNum);
            // Sum actual values for all accounts in this category
            const actualValue = category.accounts.reduce((sum, acc) => {
              const actualAcc = getActualAccountValue(acc.code);
              return sum + (actualAcc ? getMonthValue(actualAcc.monthly, monthNum) : 0);
            }, 0);
            const varianceColor = getVarianceColor(actualValue, projValue, true);

            return (
              <React.Fragment key={i}>
                <td className="min-w-[70px] px-1 py-2 text-right text-xs">
                  {formatCurrency(projValue)}
                </td>
                {showActual && (
                  <td className={`min-w-[70px] px-1 py-2 text-right text-xs bg-blue-50/50 ${varianceColor}`}>
                    {actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                  </td>
                )}
              </React.Fragment>
            );
          })}
          <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-semibold ${colorClass}`}>
            {formatCurrency(categoryTotal)}
          </td>
          {comparisonMode && visibleActualMonths.size > 0 && (() => {
            const actualCategoryTotal = category.accounts.reduce((sum, acc) => {
              const actualAcc = getActualAccountValue(acc.code);
              return sum + (actualAcc?.ytd || 0);
            }, 0);
            return (
              <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-semibold bg-blue-50/50 ${getVarianceColor(actualCategoryTotal, categoryTotal, true)}`}>
                {actualCategoryTotal !== 0 ? formatCurrency(actualCategoryTotal) : <span className="text-gray-300">--</span>}
              </td>
            );
          })()}
        </tr>

        {/* Expanded Account Rows - use pre-calculated monthly values from categoryData */}
        {isExpanded && category.accounts.map(acc => {
          let accAmount = 0;
          let percent: number | null = null;

          if (isConsolidated) {
            // For consolidated: use pre-calculated value from categoryData (based on historical proportions)
            const monthlyAcc = categoryData.accountMonthly.get(acc.code) || createEmptyMonthlyTotals();
            // Sum monthly values to get annual total
            for (let m = 1; m <= 12; m++) {
              accAmount += monthlyAcc[`month${m}` as keyof MonthlyTotals] || 0;
            }
          } else {
            // For regular locations: use expense percentage × gross
            percent = getProjectionValue(dept.name, `EXPENSE_${acc.code}`);
            accAmount = percent !== null ? Math.round((percent / 100) * projectedDeptGross) : 0;
          }
          // Use pre-calculated monthly values from categoryData
          const monthlyAcc = categoryData.accountMonthly.get(acc.code) || createEmptyMonthlyTotals();
          const actualAcc = getActualAccountValue(acc.code);

          return (
            <tr key={acc.code} className="bg-gray-50 border-b border-gray-100 hover:bg-gray-100">
              <td className="min-w-[180px] px-3 py-1.5 text-xs sticky left-0 bg-gray-50" style={{ paddingLeft: '44px' }}>
                <div className="flex items-center">
                  <span className="font-mono text-gray-600 mr-2">{acc.code}</span>
                  <span className="text-gray-500 truncate">{acc.description}</span>
                </div>
              </td>
              {MONTH_NAMES.map((_, i) => {
                const monthNum = i + 1;
                const showActual = comparisonMode && visibleActualMonths.has(monthNum);
                const projValue = getMonthValue(monthlyAcc, monthNum);
                const actualValue = actualAcc ? getMonthValue(actualAcc.monthly, monthNum) : 0;
                const varianceColor = actualAcc ? getVarianceColor(actualValue, projValue, true) : '';

                return (
                  <React.Fragment key={i}>
                    <td className="min-w-[70px] px-1 py-1.5 text-right text-xs text-gray-600">
                      {formatCurrency(projValue)}
                    </td>
                    {showActual && (
                      <td className={`min-w-[70px] px-1 py-1.5 text-right text-xs bg-blue-50/50 ${varianceColor}`}>
                        {actualAcc && actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                      </td>
                    )}
                  </React.Fragment>
                );
              })}
              <td className="min-w-[100px] px-2 py-1.5 text-right text-xs font-medium bg-gray-50">
                {formatCurrency(accAmount)}
                {percent !== null && <span className="text-gray-400 ml-1">{percent.toFixed(1)}%</span>}
              </td>
              {comparisonMode && visibleActualMonths.size > 0 && (
                <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs font-medium bg-blue-50/50 ${actualAcc ? getVarianceColor(actualAcc.ytd, accAmount, true) : ''}`}>
                  {actualAcc && actualAcc.ytd !== 0 ? formatCurrency(actualAcc.ytd) : <span className="text-gray-300">--</span>}
                </td>
              )}
            </tr>
          );
        })}
      </React.Fragment>
    );
  };


    // Resolve suffix-based pattern matching (discovers new Body Shop accounts from available data)
    const allAccountCodes = Array.from(accountMap2023.keys()).concat(Array.from(accountMap2024.keys()), Array.from(accountMap2025.keys()), Array.from(actualAccountMap.keys()), Array.from(extraDisplayAccountCodes), Array.from(inputAccountCodes));
    const resolvedCategories = resolveAccountsForDepartment(deptOriginal, allAccountCodes);
    const dept: DepartmentTemplate = resolvedCategories !== deptOriginal.expenseCategories
      ? { ...deptOriginal, expenseCategories: resolvedCategories }
      : deptOriginal;

    const isExpanded = expandedDepartments.has(dept.name);

    // Calculate seasonality weights (needed for both viewData and fallback paths)
    const deptGrossWeights = getDeptGrossSeasonalityWeights(dept.name);
    const deptExpenseWeights = getDeptExpenseSeasonalityWeights(dept.name);
    const grossIsZero = deptGrossWeights.every(w => Math.abs(w - 1/12) < 0.0001);
    const deptWeights = grossIsZero ? deptExpenseWeights : deptGrossWeights;

    // Get department data from viewData (required - no fallback)
    const viewDataDept = getViewDataDepartment(dept.name);

    // Use viewData for department totals
    let projectedGross = 0;
    let projectedUnits = 0;
    let monthlyGross: MonthlyTotals = createEmptyMonthlyTotals();
    let monthlyExpenses: MonthlyTotals = createEmptyMonthlyTotals();
    let projectedExpenses = 0;

    if (viewDataDept) {
      projectedGross = viewDataDept.projectedGross;
      projectedExpenses = viewDataDept.projectedExpenses;
      projectedUnits = viewDataDept.projectedUnits || 0;
      monthlyGross = viewDataDept.monthlyGross;
      monthlyExpenses = viewDataDept.monthlyExpenses;
    } else {
      // viewData is required for each department - log error if missing
      console.error(`SeasonalityModeView: viewData missing for department ${dept.name}`);
    }

    // Build categoryDataMap for expense drill-down (needed regardless of viewData)
    const categoryDataMap = new Map<string, { annual: number; monthly: MonthlyTotals; accountMonthly: Map<string, MonthlyTotals> }>();

    if (isConsolidated) {
      // For consolidated view, build categoryDataMap from expensesByAccount for drill-down support
      dept.expenseCategories
        .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
        .forEach(cat => {
          let categoryAnnual = 0;
          let categoryMonthly = createEmptyMonthlyTotals();
          const accountMonthly = new Map<string, MonthlyTotals>();

          cat.accounts.forEach(acc => {
            const expenseKey = `${dept.name}#EXPENSE_${acc.code}`;
            const preCalculated = expensesByAccount?.[expenseKey];
            if (preCalculated) {
              categoryAnnual += preCalculated.annualTotal;
              const accMonthly: MonthlyTotals = {
                month1: preCalculated.monthlyDistribution.month1 || 0,
                month2: preCalculated.monthlyDistribution.month2 || 0,
                month3: preCalculated.monthlyDistribution.month3 || 0,
                month4: preCalculated.monthlyDistribution.month4 || 0,
                month5: preCalculated.monthlyDistribution.month5 || 0,
                month6: preCalculated.monthlyDistribution.month6 || 0,
                month7: preCalculated.monthlyDistribution.month7 || 0,
                month8: preCalculated.monthlyDistribution.month8 || 0,
                month9: preCalculated.monthlyDistribution.month9 || 0,
                month10: preCalculated.monthlyDistribution.month10 || 0,
                month11: preCalculated.monthlyDistribution.month11 || 0,
                month12: preCalculated.monthlyDistribution.month12 || 0,
              };
              categoryMonthly = addMonthlyTotals(categoryMonthly, accMonthly);
              accountMonthly.set(acc.code, accMonthly);
            }
          });

          if (categoryAnnual > 0 || accountMonthly.size > 0) {
            categoryDataMap.set(cat.name, { annual: categoryAnnual, monthly: categoryMonthly, accountMonthly });
          }
        });
    } else {
      // For regular locations, build categoryDataMap for expense drill-down
      dept.expenseCategories
        .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
        .forEach(cat => {
          const categoryData = getCategoryMonthlyExpense(cat, dept.name, projectedGross, deptWeights);
          categoryDataMap.set(cat.name, categoryData);
        });
    }

    const projectedProfit = projectedGross - projectedExpenses;
    const hasProjections = projectedGross > 0 || projectedExpenses > 0;

    // Calculate monthly profit as the difference (Gross - Expenses)
    const monthlyProfit = createEmptyMonthlyTotals();
    for (let m = 1; m <= 12; m++) {
      const key = `month${m}` as keyof MonthlyTotals;
      monthlyProfit[key] = monthlyGross[key] - monthlyExpenses[key];
    }

    return (
      <div className="bg-white rounded-lg shadow mb-4 print-department-section">
        {/* Department Header */}
        <DepartmentHeader
          dept={dept}
          isExpanded={isExpanded}
          toggleDepartment={toggleDepartment}
          projectedGross={projectedGross}
          projectedExpenses={projectedExpenses}
          projectedProfit={projectedProfit}
          projectedUnits={projectedUnits}
          hasProjections={hasProjections}
          comparisonMode={comparisonMode || false}
          expandedCharts={expandedCharts}
          toggleChart={toggleChart}
          formatCurrency={formatCurrency}
          targetYear={targetYear}
        />

        {/* Expanded Content */}
        {isExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              {/* Column Headers */}
              <DepartmentTableHeader
                comparisonMode={comparisonMode || false}
                visibleActualMonths={visibleActualMonths}
                actualData={actualData}
                toggleActualMonth={toggleActualMonth}
                targetYear={targetYear}
              />
              <tbody>
                {/* NEW/USED DEPARTMENTS: Units, Gross, F&I, Total Gross */}
                {dept.hasFandI && (
                  <>
                    {/* Units by Brand Section */}
                    {(() => {
                      const brandUnits = getProjectedUnitsByBrand(dept.name);
                      const brands = Object.keys(brandUnits).filter(b => brandUnits[b] > 0);
                      const totalUnits = getTotalProjectedUnits(dept.name);

                      if (brands.length === 0 && totalUnits === 0) return null;

                      const headerColorClass = dept.name === 'NEW_VEHICLE' ? 'bg-blue-100 text-blue-900' : 'bg-amber-100 text-amber-900';
                      const colorClass = dept.name === 'NEW_VEHICLE' ? 'bg-blue-50 text-blue-900' : 'bg-amber-50 text-amber-900';
                      const totalColorClass = dept.name === 'NEW_VEHICLE' ? 'bg-blue-200 text-blue-900' : 'bg-amber-200 text-amber-900';

                      return (
                        <>
                          {/* Per-brand breakdown - hidden for consolidated view */}
                          {!isConsolidated && (
                            <>
                              <tr className={headerColorClass}>
                                <td colSpan={14} className={`px-3 py-2 text-sm font-bold ${headerColorClass}`}>
                                  Units Sold by Brand (Monthly)
                                </td>
                              </tr>
                              {brands.map(brand => {
                                const units = brandUnits[brand];
                                // Use brand-specific unit seasonality, not department gross seasonality
                                const brandUnitWeights = getBrandUnitSeasonalityWeights(dept.name, brand);
                                const monthlyUnits = applySeasonality(units, brandUnitWeights);
                                // Get actual brand unit data for comparison mode
                                const actualBrandMonthly = dept.name === 'NEW_VEHICLE'
                                  ? actualUnitCounts?.monthlyNewUnitsByBrand?.[brand]
                                  : actualUnitCounts?.monthlyUsedUnitsByBrand?.[brand];
                                const actualBrandTotal = dept.name === 'NEW_VEHICLE'
                                  ? actualUnitCounts?.newVehicleUnits?.[brand] || 0
                                  : actualUnitCounts?.usedVehicleUnits?.[brand] || 0;
                                return renderUnitMonthlyRow(brand, monthlyUnits, units, colorClass, 1, actualBrandMonthly, actualBrandTotal);
                              })}
                            </>
                          )}
                          {(() => {
                            // Stored UNITS spread when it matches the total (carries Executive
                            // month adjustments); department-level unit seasonality otherwise
                            const deptUnitWeights = getDeptUnitSeasonalityWeights(dept.name);
                            const monthlyTotalUnits = getStoredMonthlyDistribution(dept.name, 'UNITS', totalUnits, deptUnitWeights);
                            // Get actual department unit data for comparison mode
                            const actualDeptMonthly = dept.name === 'NEW_VEHICLE'
                              ? actualUnitCounts?.monthlyNewUnits
                              : actualUnitCounts?.monthlyUsedUnits;
                            const actualDeptTotal = dept.name === 'NEW_VEHICLE'
                              ? actualUnitCounts?.totalNewUnits || 0
                              : actualUnitCounts?.totalUsedUnits || 0;

                            return (
                              <VehicleTotalUnitsRow
                                totalUnits={totalUnits}
                                monthlyTotalUnits={monthlyTotalUnits}
                                actualDeptMonthly={actualDeptMonthly}
                                actualDeptTotal={actualDeptTotal}
                                totalColorClass={totalColorClass}
                                comparisonMode={comparisonMode || false}
                                visibleActualMonths={visibleActualMonths}
                                labelAdornment={adjustmentAdornment('UNITS')}
                              />
                            );
                          })()}
                        </>
                      );
                    })()}

                    {/* Gross (Frontend) */}
                    {(() => {
                      const grossCalc = getCalculatedGross(dept.name, 'GROSS');
                      // For consolidated: use stored monthly values (actual sums from all locations)
                      // For regular: apply seasonality to per-unit calculation
                      // Regular: the stored front-gross spread when it matches the total (carries
                      // Executive month adjustments), else seasonality on the per-unit calculation
                      const monthlyGross = isConsolidated
                        ? (calculationsMap.get(`${dept.name}#GROSS`) || applySeasonality(grossCalc.total, deptWeights))
                        : getStoredMonthlyDistribution(dept.name, 'GROSS', grossCalc.total, deptWeights);
                      const actualFrontGross = getActualFrontGross(dept.name);
                      // For consolidated, show "~avg" since per-unit varies by location
                      const perUnitLabel = isConsolidated
                        ? `~${formatCurrency(grossCalc.perUnit)}/u avg`
                        : `${formatCurrency(grossCalc.perUnit)}/u`;
                      return renderMonthlyRow(
                        `Gross (Frontend) - ${perUnitLabel}`,
                        monthlyGross,
                        grossCalc.total,
                        ROW_COLORS.gross,
                        true,
                        0,
                        actualFrontGross?.monthly,
                        actualFrontGross?.ytd,
                        false,
                        adjustmentAdornment('GROSS')
                      );
                    })()}

                    {/* F&I (Backend) */}
                    {(() => {
                      const fiCalc = getCalculatedGross(dept.name, 'FI_INCOME');
                      // For consolidated: use stored monthly values (actual sums from all locations)
                      // For regular: apply seasonality to per-unit calculation
                      const monthlyFI = isConsolidated
                        ? (calculationsMap.get(`${dept.name}#FI_INCOME`) || calculationsMap.get(`${dept.name}#F_AND_I`) || applySeasonality(fiCalc.total, deptWeights))
                        : getStoredMonthlyDistribution(dept.name, 'FI_INCOME', fiCalc.total, deptWeights);
                      const actualFI = getActualFIGross(dept.name);
                      // For consolidated, show "~avg" since per-unit varies by location
                      const perUnitLabel = isConsolidated
                        ? `~${formatCurrency(fiCalc.perUnit)}/u avg`
                        : `${formatCurrency(fiCalc.perUnit)}/u`;
                      return renderMonthlyRow(
                        `F&I (Backend) - ${perUnitLabel}`,
                        monthlyFI,
                        fiCalc.total,
                        ROW_COLORS.fiIncome,
                        true,
                        0,
                        actualFI?.monthly,
                        actualFI?.ytd,
                        false,
                        adjustmentAdornment('FI_INCOME')
                      );
                    })()}

                    {/* Total Gross */}
                    {(() => {
                      const actualGross = getActualDeptGross(dept.name);
                      return renderMonthlyRow('Total Gross', monthlyGross, projectedGross, ROW_COLORS.totalGross, true, 0,
                        actualGross?.monthly, actualGross?.ytd, false);
                    })()}
                  </>
                )}

                {/* SERVICE/PARTS/BODY SHOP: Sales, Gross */}
                {dept.hasRevenue && !dept.hasFandI && (
                  <>
                    {(() => {
                      const serviceDeptProj = getServiceDeptProjections(dept.name);
                      const monthlySales = applySeasonality(serviceDeptProj.calculatedSales, deptWeights);
                      // Use stored CALCULATED records to match Forecast page purple line
                      const monthlyGross = getStoredMonthlyDistribution(dept.name, 'GROSS', serviceDeptProj.gross, deptGrossWeights);
                      const actualGross = getActualDeptGross(dept.name);
                      const actualSales = getActualDeptSales(dept.name);
                      const payTypeData = getServicePayTypeBreakdown(dept.name);
                      const partsPayTypeData = getPartsPayTypeBreakdown(dept.name);
                      const payTypeKey = `${dept.name}-GROSS-PAYTYPE`;
                      const isPayTypeExpanded = expandedCategories.has(payTypeKey);
                      const canExpandPayType = comparisonMode && (!!payTypeData || !!partsPayTypeData);

                      return (
                        <>
                          {renderMonthlyRow('Sales (Calculated)', monthlySales, serviceDeptProj.calculatedSales, ROW_COLORS.sales, true, 0,
                            actualSales?.monthly, actualSales?.ytd, false)}
                          {/* Gross row - clickable to expand pay-type breakdown when actuals available */}
                          <tr
                            className={`${ROW_COLORS.gross} border-b border-gray-200 ${canExpandPayType ? 'cursor-pointer hover:opacity-90' : ''}`}
                            onClick={() => canExpandPayType && toggleCategory(payTypeKey)}
                          >
                            <td className="px-2 py-2 text-xs font-bold whitespace-nowrap">
                              <span className="flex items-center gap-1">
                                {canExpandPayType && (
                                  <span className="text-[10px] text-green-600 w-3">{isPayTypeExpanded ? '▼' : '▶'}</span>
                                )}
                                {`Gross - ${serviceDeptProj.grossToSalesPercent.toFixed(1)}% to Sales`}
                                {adjustmentAdornment('GROSS')}
                              </span>
                            </td>
                            {MONTH_NAMES.map((_, i) => {
                              const monthNum = i + 1;
                              const projectedValue = getMonthValue(monthlyGross, monthNum);
                              const showActual = comparisonMode && visibleActualMonths.has(monthNum);
                              const actualValue = actualGross ? getMonthValue(actualGross.monthly, monthNum) : 0;
                              return (
                                <React.Fragment key={i}>
                                  <td className="min-w-[70px] px-1 py-2 text-right text-xs font-bold">
                                    {formatCurrency(projectedValue)}
                                  </td>
                                  {showActual && (
                                    <td className={`min-w-[70px] px-1 py-2 text-right text-xs font-bold bg-blue-50/50 ${actualGross ? getVarianceColor(actualValue, projectedValue, false) : ''}`}>
                                      {actualGross && actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                                    </td>
                                  )}
                                </React.Fragment>
                              );
                            })}
                            <td className="min-w-[80px] px-1 py-2 text-right text-xs font-bold border-l border-gray-300">
                              {formatCurrency(serviceDeptProj.gross)}
                            </td>
                            {comparisonMode && (
                              <td className={`min-w-[80px] px-1 py-2 text-right text-xs font-bold border-l border-gray-300 bg-blue-50/50 ${actualGross ? getVarianceColor(actualGross.ytd, serviceDeptProj.gross, false) : ''}`}>
                                {actualGross ? formatCurrency(actualGross.ytd) : <span className="text-gray-300">--</span>}
                              </td>
                            )}
                          </tr>
                          {/* Service pay-type breakdown sub-rows (actuals only) */}
                          {isPayTypeExpanded && payTypeData && (() => {
                            const payTypes = [
                              { key: 'customer', label: 'Customer Labor' },
                              { key: 'warranty', label: 'Warranty Labor' },
                              { key: 'internal', label: 'Internal Labor' },
                              { key: 'other', label: 'Other (Sublet/Towing)' },
                            ] as const;
                            return payTypes.map(pt => {
                              const ptData = payTypeData[pt.key];
                              return (
                                <tr key={`svc-${pt.key}`} className="bg-green-50/40 border-b border-gray-100">
                                  <td className="min-w-[180px] px-3 py-1.5 text-xs font-medium sticky left-0 bg-green-50/40 text-green-700" style={{ paddingLeft: '28px' }}>
                                    {pt.label}
                                  </td>
                                  {MONTH_NAMES.map((_, i) => {
                                    const monthNum = i + 1;
                                    const showActual = comparisonMode && visibleActualMonths.has(monthNum);
                                    const actualValue = getMonthValue(ptData.gross, monthNum);
                                    return (
                                      <React.Fragment key={i}>
                                        {/* Projected column shows -- for pay-type breakdown */}
                                        <td className="min-w-[70px] px-1 py-1.5 text-right text-xs text-gray-300">
                                          --
                                        </td>
                                        {showActual && (
                                          <td className="min-w-[70px] px-1 py-1.5 text-right text-xs bg-blue-50/50">
                                            {actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                                          </td>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                  {/* Annual projected shows -- */}
                                  <td className="min-w-[80px] px-1 py-1.5 text-right text-xs border-l border-gray-300 text-gray-300">
                                    --
                                  </td>
                                  {/* Annual actual */}
                                  {comparisonMode && (
                                    <td className="min-w-[80px] px-1 py-1.5 text-right text-xs border-l border-gray-300 bg-blue-50/50">
                                      {ptData.ytdGross !== 0 ? formatCurrency(ptData.ytdGross) : <span className="text-gray-300">--</span>}
                                    </td>
                                  )}
                                </tr>
                              );
                            });
                          })()}
                          {/* Parts pay-type breakdown sub-rows (actuals only) */}
                          {isPayTypeExpanded && partsPayTypeData && (() => {
                            const partsPayTypes = [
                              { key: 'customerParts', label: 'Customer Parts' },
                              { key: 'warrantyParts', label: 'Warranty Parts' },
                              { key: 'internalParts', label: 'Internal Parts' },
                              { key: 'counterRetail', label: 'Counter Retail' },
                              { key: 'counterWholesale', label: 'Counter Wholesale' },
                              { key: 'otherParts', label: 'Other Parts' },
                              { key: 'inventoryAdjustment', label: 'Inventory Adjustment' },
                            ] as const;
                            return partsPayTypes.map(pt => {
                              const ptData = partsPayTypeData[pt.key];
                              return (
                                <tr key={`parts-${pt.key}`} className="bg-green-50/40 border-b border-gray-100">
                                  <td className="min-w-[180px] px-3 py-1.5 text-xs font-medium sticky left-0 bg-green-50/40 text-green-700" style={{ paddingLeft: '28px' }}>
                                    {pt.label}
                                  </td>
                                  {MONTH_NAMES.map((_, i) => {
                                    const monthNum = i + 1;
                                    const showActual = comparisonMode && visibleActualMonths.has(monthNum);
                                    const actualValue = getMonthValue(ptData.gross, monthNum);
                                    return (
                                      <React.Fragment key={i}>
                                        <td className="min-w-[70px] px-1 py-1.5 text-right text-xs text-gray-300">
                                          --
                                        </td>
                                        {showActual && (
                                          <td className="min-w-[70px] px-1 py-1.5 text-right text-xs bg-blue-50/50">
                                            {actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
                                          </td>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                  <td className="min-w-[80px] px-1 py-1.5 text-right text-xs border-l border-gray-300 text-gray-300">
                                    --
                                  </td>
                                  {comparisonMode && (
                                    <td className="min-w-[80px] px-1 py-1.5 text-right text-xs border-l border-gray-300 bg-blue-50/50">
                                      {ptData.ytdGross !== 0 ? formatCurrency(ptData.ytdGross) : <span className="text-gray-300">--</span>}
                                    </td>
                                  )}
                                </tr>
                              );
                            });
                          })()}
                        </>
                      );
                    })()}
                  </>
                )}

                {/* RENTAL: Rental Income */}
                {dept.name === 'RENTAL' && (
                  <>
                    {(() => {
                      const rentalIncome = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
                      // Use stored CALCULATED records to match Forecast page purple line
                      const monthlyRental = getStoredMonthlyDistribution(dept.name, 'RENTAL_INCOME', rentalIncome, deptGrossWeights);
                      const actualRentalGross = getActualDeptGross(dept.name);
                      return renderMonthlyRow('Rental Vehicle Income', monthlyRental, rentalIncome, ROW_COLORS.rentalIncome, true, 0,
                        actualRentalGross?.monthly, actualRentalGross?.ytd, false, adjustmentAdornment('RENTAL_INCOME'));
                    })()}
                  </>
                )}

                {/* OTHERS: Income Accounts */}
                {dept.name === 'OTHERS' && (() => {
                  const otherIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'OTHER_INCOME');
                  const categoryKey = `${dept.name}-OTHER_INCOME`;
                  const isIncomeExpanded = expandedCategories.has(categoryKey);
                  const accountsWithData = otherIncomeCategory?.accounts.filter(acc => {
                    return hasAccountData(acc.code);
                  }) || [];
                  const totalProjectedUnits = getTotalProjectedUnits('NEW_VEHICLE') + getTotalProjectedUnits('USED_VEHICLE');

                  const actualOthersGross = getActualDeptGross(dept.name);
                  return (
                    <OthersIncomeContent
                      dept={dept}
                      comparisonMode={comparisonMode || false}
                      visibleActualMonths={visibleActualMonths}
                      formatCurrency={formatCurrency}
                      isConsolidated={isConsolidated}
                      targetYear={targetYear}
                      otherIncomeCategory={otherIncomeCategory}
                      isIncomeExpanded={isIncomeExpanded}
                      toggleCategory={toggleCategory}
                      monthlyGross={monthlyGross}
                      projectedGross={projectedGross}
                      accountsWithData={accountsWithData}
                      totalProjectedUnits={totalProjectedUnits}
                      getProjectionValue={getProjectionValue}
                      applySeasonality={applySeasonality}
                      getAccountSeasonalityWeights={getAccountSeasonalityWeights}
                      deptWeights={deptWeights}
                      expensesByAccount={expensesByAccount}
                      actualGross={actualOthersGross}
                      getActualAccountValue={getActualAccountValue}
                    />
                  );
                })()}

                {/* EXPENSE CATEGORIES - use pre-calculated category data for bottom-up aggregation */}
                {dept.name === 'RENTAL' && (
                  <>
                    {dept.expenseCategories
                      .filter(cat => cat.name === 'RENTAL_SELLING')
                      .map(cat => {
                        const categoryData = categoryDataMap.get(cat.name) || { annual: 0, monthly: createEmptyMonthlyTotals(), accountMonthly: new Map() };
                        return renderExpenseCategory(dept, cat, categoryData, projectedGross);
                      })}
                    {dept.expenseCategories
                      .filter(cat => cat.name === 'RENTAL_FIXED')
                      .map(cat => {
                        const categoryData = categoryDataMap.get(cat.name) || { annual: 0, monthly: createEmptyMonthlyTotals(), accountMonthly: new Map() };
                        return renderExpenseCategory(dept, cat, categoryData, projectedGross);
                      })}
                  </>
                )}

                {dept.name !== 'RENTAL' && dept.name !== 'OTHERS' && dept.expenseCategories
                  .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
                  .map(cat => {
                    const categoryData = categoryDataMap.get(cat.name) || { annual: 0, monthly: createEmptyMonthlyTotals(), accountMonthly: new Map() };
                    return renderExpenseCategory(dept, cat, categoryData, projectedGross);
                  })}

                {/* Total Expenses */}
                {dept.name !== 'MISC' && dept.name !== 'OTHERS' && projectedExpenses > 0 && (() => {
                  const actualExpenses = getActualDeptExpenses(dept.name);
                  return renderMonthlyRow('Total Expenses', monthlyExpenses, projectedExpenses, ROW_COLORS.totalExpense, true, 0,
                    actualExpenses?.monthly, actualExpenses?.ytd, true);
                })()}

                {/* Operating Profit */}
                {dept.name !== 'MISC' && dept.name !== 'OTHERS' && projectedGross > 0 && (() => {
                  const actualProfit = getActualDeptProfit(dept.name);
                  return renderMonthlyRow(
                    'Operating Profit',
                    monthlyProfit,
                    projectedProfit,
                    projectedProfit >= 0 ? ROW_COLORS.profit : ROW_COLORS.loss,
                    true,
                    0,
                    actualProfit?.monthly,
                    actualProfit?.ytd,
                    false
                  );
                })()}
              </tbody>
            </table>
          </div>
        )}
        {editorMetric && monthlyAdjustment && adjustableRows.length > 0 && (
          <MonthlyAdjustmentEditor
            locationId={monthlyAdjustment.locationId}
            targetYear={monthlyAdjustment.targetYear}
            department={dept.name}
            departmentLabel={dept.displayName}
            rows={adjustableRows}
            initialMetric={editorMetric}
            formatCurrency={formatCurrency}
            onClose={() => setEditorMetric(null)}
            onSaved={monthlyAdjustment.onSaved}
          />
        )}

        {/* Department Comparison Chart */}
        <DepartmentComparisonChartSection
          dept={dept}
          comparisonMode={comparisonMode || false}
          expandedCharts={expandedCharts}
          monthlyGross={monthlyGross}
          monthlyExpenses={monthlyExpenses}
          monthlyProfit={monthlyProfit}
          projectedUnits={projectedUnits}
          getActualDeptGross={getActualDeptGross}
          getActualDeptExpenses={getActualDeptExpenses}
          getActualDeptProfit={getActualDeptProfit}
          getDeptUnitSeasonalityWeights={getDeptUnitSeasonalityWeights}
          applySeasonality={applySeasonality}
          actualUnitCounts={actualUnitCounts}
        />
      </div>
    );
};
